import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiSearch, FiX } from 'react-icons/fi';
import usePopoverAnclado from '../../hooks/usePopoverAnclado';
import '../../css/selector-multiple.css';

// A partir de esta cantidad, recorrer la lista a ojo deja de ser viable
const MINIMO_PARA_BUSCAR = 8;

/**
 * Selector de varios valores para barras de filtros.
 *
 * El botón resume lo elegido ("Estado: Abierto +1") en vez de obligar a abrir
 * el panel para saber qué hay activo, y cada opción lleva su recuento, que es
 * lo que evita filtrar hacia un resultado vacío.
 *
 * @param opciones  [{ valor, etiqueta, conteo }]
 * @param valores   array de valores seleccionados
 * @param onChange  recibe el array nuevo
 * @param etiqueta  nombre del filtro, visible en el botón
 */
const SelectorMultiple = ({ etiqueta, opciones, valores, onChange, ancho = 250, alinear = 'izquierda' }) => {
  const { t } = useTranslation();
  const [busqueda, setBusqueda] = useState('');
  const { abierto, alternar, cerrar, refBoton, refPanel, estilo } = usePopoverAnclado({ ancho, alinear });

  const conBuscador = opciones.length >= MINIMO_PARA_BUSCAR;

  const visibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    if (!texto) return opciones;
    return opciones.filter((o) => o.etiqueta.toLowerCase().includes(texto));
  }, [opciones, busqueda]);

  const alternarValor = (valor) => {
    onChange(valores.includes(valor) ? valores.filter((v) => v !== valor) : [...valores, valor]);
  };

  const elegidas = opciones.filter((o) => valores.includes(o.valor));
  const resumen = elegidas.length === 0
    ? null
    : elegidas.length === 1
      ? elegidas[0].etiqueta
      : `${elegidas[0].etiqueta} +${elegidas.length - 1}`;

  return (
    <>
      <button
        type="button"
        ref={refBoton}
        className={`selector-multiple-boton ${abierto ? 'sm-abierto' : ''} ${resumen ? 'sm-con-valor' : ''}`}
        onClick={alternar}
        aria-haspopup="true"
        aria-expanded={abierto}
      >
        <span className="selector-multiple-etiqueta">{etiqueta}</span>
        {resumen && <span className="selector-multiple-resumen">{resumen}</span>}
        {resumen ? (
          // Quitar el filtro sin abrir el panel: es lo que más se repite
          <span
            role="button"
            tabIndex={0}
            className="selector-multiple-limpiar"
            aria-label={t('comun.cerrarFiltro')}
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onChange([]); } }}
          >
            <FiX />
          </span>
        ) : (
          <FiChevronDown className="selector-multiple-flecha" />
        )}
      </button>

      {abierto && estilo && createPortal(
        <div ref={refPanel} className="selector-multiple-panel" style={estilo} role="dialog" aria-label={etiqueta}>
          {conBuscador && (
            <div className="selector-multiple-buscador">
              <FiSearch aria-hidden="true" />
              <input
                type="text"
                value={busqueda}
                autoFocus
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder={t('comun.buscar')}
                aria-label={t('comun.buscar')}
              />
            </div>
          )}

          <div className="selector-multiple-lista">
            {visibles.length === 0 && <p className="selector-multiple-vacio">{t('comun.sinResultados')}</p>}
            {visibles.map((opcion) => (
              <label key={opcion.valor} className="selector-multiple-opcion">
                <input
                  type="checkbox"
                  checked={valores.includes(opcion.valor)}
                  onChange={() => alternarValor(opcion.valor)}
                />
                {opcion.punto && <span className={`selector-multiple-punto ${opcion.punto}`} aria-hidden="true" />}
                <span className="selector-multiple-texto">{opcion.etiqueta}</span>
                {opcion.conteo !== undefined && <span className="selector-multiple-conteo">{opcion.conteo}</span>}
              </label>
            ))}
          </div>

          <div className="selector-multiple-pie">
            <button type="button" onClick={() => onChange([])} disabled={valores.length === 0}>
              {t('comun.limpiarSeleccion')}
            </button>
            <button type="button" className="primario" onClick={() => cerrar()}>{t('comun.hecho')}</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default SelectorMultiple;
